import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

export interface MenuItem {
  readonly label: string;
  readonly icon?: IconName;
  readonly danger?: boolean;
  readonly onSelect: () => void;
  readonly testId?: string;
}

/** "⋯" button (or a custom trigger) with a small drop-down list of actions. */
export function MenuButton({
  label,
  items,
  trigger,
  testId,
}: {
  /** Accessible name of the button, e.g. "Actions for Hall North". */
  label: string;
  items: readonly MenuItem[];
  /** Visible content instead of the "⋯" icon, e.g. the user's name. */
  trigger?: ReactNode;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    root.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="menu" ref={root}>
      <button
        type="button"
        className={trigger ? 'btn' : 'btn btn-ghost btn-icon'}
        aria-label={label}
        title={trigger ? undefined : label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        data-testid={testId}
      >
        {trigger ?? <Icon name="more" size={18} />}
      </button>
      {open && (
        <div className="menu-list" role="menu">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`menu-item${item.danger ? ' is-danger' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(false);
                item.onSelect();
              }}
              data-testid={item.testId}
            >
              {item.icon && <Icon name={item.icon} />}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
